/* eslint-disable @typescript-eslint/no-explicit-any */
import SbTextField from '@Components/Textfield';
import { Box, Divider, Grid, Paper, TextField, Typography } from '@mui/material';
import { AppDispatch, RootStateType } from '@Store';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from '@Components/DatePicker';
import SBCountryDropdown from '@Components/Dropdown/Country';
import dayjs from 'dayjs';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import SbButton from '@Components/Button';
import {
  onForward,
  PaymentTypeOptions,
} from '../../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/BlueDart/constants';
import * as Yup from 'yup';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';

interface propsTypes {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const bluedartInformationSchema = Yup.object().shape({
  type: Yup.string().trim().required('Type is required'),
  countryOfManufacture: Yup.string().trim().required('Country of manufacture is required'),
  invoiceDate: Yup.date().required('Invoice Date is required').nullable(),
  pickupTime: Yup.string()
    .required('Pickup Time is required')
    .matches(/^([01]?[0-9]|2[0-3])[0-5][0-9]$/, 'Invalid time format (HHMM)'),
  // .test(
  //   'pickup-time-validation',
  //   "Pickup time must be after current time for today's invoice date",
  //   function (value) {
  //     const { invoiceDate } = this.parent;
  //     if (!invoiceDate || !value) return true; // if no date or no pickup time, skip this validation

  //     const selectedDate = dayjs(invoiceDate);
  //     const today = dayjs();

  //     if (selectedDate.isSame(today, 'day')) {
  //       const now = dayjs();
  //       const currentMinutes = now.hour() * 60 + now.minute();

  //       // Convert pickupTime (e.g., '1715') into minutes
  //       const pickupHours = parseInt(value.substring(0, 2), 10);
  //       const pickupMinutes = parseInt(value.substring(2, 4), 10);
  //       const pickupTotalMinutes = pickupHours * 60 + pickupMinutes;

  //       // Pickup time must be at least 15 minutes after current time
  //       return pickupTotalMinutes >= currentMinutes + 15;
  //     }

  //     return true; // if not today, no restriction
  //   },
  // ),
  subProductCode: Yup.array().min(1, 'Sub Product Code is required').required('Sub Product Code is required'),

  invoiceNumber: Yup.string().trim().required('Invoice number is required'),
  accountNo: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Account value is required'),
        display: Yup.string().required('Account display is required'),
      }),
    )
    .min(1, 'At least one account number is required')
    .required('Account number is required'),
});

const UserBluedart = (props: propsTypes) => {
  const { onClickForward, onClose, initialStates, isEditable, showFedExDetails, btnText } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { bluedartDetails } = useSelector((state: RootStateType) => state.adminShipment);
  const invoiceNo = useSelector((state: RootStateType) => state.shipment.shipmentById?._packageId.invoiceNumber);
  const { shipmentById, userBluedartInformationDetails: bluedartSettings } = useSelector(
    (state: RootStateType) => state.shipment,
  );

  const { values, handleChange, setFieldValue, errors, handleSubmit } = useFormik<any>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      ...initialStates,
      invoiceNumber: invoiceNo,
      accountNo: bluedartSettings?.accountNo
        ? bluedartSettings?.accountNo.filter((acc: any) => acc.value.startsWith(initialStates.accountNo))
        : [],
    },
    validationSchema: bluedartInformationSchema,
    onSubmit(values) {
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
      });
    },
  });

  const subProductOptions = PaymentTypeOptions.filter(option =>
    bluedartSettings?.subProductCode.includes(option.value as any),
  );

  return (
    <>
      {showFedExDetails && (
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">Bluedart Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SbTextField
                    disabled={!isEditable}
                    label="Invoice Number"
                    required
                    value={values.invoiceNumber}
                    placeholder="12345678"
                    onChange={handleChange}
                    name="invoiceNumber"
                    error={errors.invoiceNumber?.toString() as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBCountryDropdown
                    disabled={!isEditable}
                    required
                    label="Country of Manufacture"
                    value={values?.countryOfManufacture}
                    options={undefined}
                    onChange={value => {
                      setFieldValue('countryOfManufacture', value[0]);
                    }}
                    error={errors?.countryOfManufacture as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    required
                    label="Invoice Date"
                    value={values?.invoiceDate || null}
                    handleChange={value => {
                      setFieldValue('invoiceDate', value);
                    }}
                    minDate={dayjs()}
                    disabled={!isEditable}
                    error={errors.invoiceDate as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="subProductCode"
                    label="Sub Product Code"
                    required
                    placeholder="Select "
                    name="subProductCode"
                    value={values.subProductCode ? [values.subProductCode] : []}
                    options={subProductOptions}
                    onChange={value => {
                      setFieldValue('subProductCode', value);
                    }}
                    error={errors.subProductCode as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body1" component="label" sx={{ fontSize: '14px', fontWeight: 500 }}>
                      Pickup Time *
                    </Typography>
                    <TextField
                      type="time"
                      value={
                        values.pickupTime
                          ? `${values.pickupTime.substring(0, 2)}:${values.pickupTime.substring(2)}`
                          : ''
                      }
                      onChange={e => {
                        const time = e.target.value.replace(/:/g, '');
                        setFieldValue('pickupTime', time);
                      }}
                      disabled={!isEditable}
                      required
                      error={!!errors.pickupTime}
                      helperText={(errors.pickupTime as string) || 'Select time in 24hr format'}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        step: 300, // 5 min intervals
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label="Bluedart Account"
                    required
                    placeholder="Select"
                    name="accountNo"
                    value={
                      Array.isArray(values?.accountNo)
                        ? values.accountNo.map((item: { value: any }) => item.value)
                        : values.accountNo
                        ? [values.accountNo]
                        : []
                    }
                    options={bluedartSettings?.accountNo ?? []}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={errors.accountNo as string}
                  />
                </Grid>
              </Grid>
              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: '4px !important' }}
                      className="w-full"
                      onClick={_event => {
                        handleSubmit();
                      }}
                    >
                      {btnText}
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        sx={{ borderRadius: '4px !important' }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}
      {bluedartDetails?.response && (
        <Grid container item>
          <Grid item xs={'auto'} border={'1px solid'} className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component={'span'}
              className="font-medium md-text ml-1"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById?.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default UserBluedart;
